import { useState, useEffect, useRef, useCallback } from 'react';

export interface UseTimerReturn {
  timeRemaining: number;
  isRunning: boolean;
  start: () => void;
  pause: () => void;
  reset: (newLimit: number) => void;
}

export function useTimer(initialLimit: number, onExpire: () => void): UseTimerReturn {
  const [timeRemaining, setTimeRemaining] = useState(initialLimit);
  const [isRunning, setIsRunning] = useState(false);
  const onExpireRef = useRef(onExpire);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    if (!isRunning) return;
    intervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setIsRunning(false);
          onExpireRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning]);

  const start = useCallback(() => setIsRunning(true), []);
  const pause = useCallback(() => setIsRunning(false), []);
  const reset = useCallback((newLimit: number) => {
    setIsRunning(false);
    setTimeRemaining(newLimit);
  }, []);

  return { timeRemaining, isRunning, start, pause, reset };
}
